import { formatISO, startOfDay, startOfToday, subDays, subMonths } from "date-fns";
import { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useActiveOrganization } from "common/account/active_organization";
import { Badge } from "common/core/badge";
import { Hr } from "common/core/horizontal_rule";
import LoadingIndicator from "common/core/loading_indicator";
import { Heading, Paragraph } from "common/core/typography";
import { ReportingIntervalEnum } from "graphql_globals";
import { useQuery } from "util/graphql";
import { useViewer } from "util/viewer_wrapper";
// eslint-disable-next-line import/no-restricted-paths
import type { TitleRouterViewer_viewer as TitleViewer } from "title_portal/router/viewer_query.graphql";
// eslint-disable-next-line import/no-restricted-paths
import type { BusinessRouterViewer_viewer as BusinessViewer } from "business_portal/router/viewer_query.graphql";

import { CompletedDetailsSection } from "./completed_details_section";
import Styles from "./index.module.scss";
import ReportsDetails from "./index.query.graphql";
import { NotaryMeetingFeedbackSection } from "./notary_meeting_feedback_section";
import { SentCompletedSection } from "./sent_completed_section";
import {
  isNotarizeCountScalarHashArray,
  isNotarizeSentCompletedScalarHashArray,
  isNotarizeSummaryScalarHashArray,
} from "./shared/utils";
import { ValueSection } from "./value_section";

const MESSAGES = defineMessages({
  title: {
    id: "57e1be81-29a1-49e1-80e7-75e7f6cdb1e1",
    defaultMessage: "{orgName} analytics",
  },
  titleTag: {
    id: "31d8d26a-9eed-41a5-b47e-22695ced9c97",
    defaultMessage: "Beta",
  },
  subtitle: {
    id: "9c2eb11b-2765-418a-acbd-ec466477a8d9",
    defaultMessage: "Reports give you an in-depth look at trends in your data.",
  },
  viewingLast30Days: {
    id: "fb344846-a509-424d-a9f0-8af0e331ca08",
    defaultMessage: "Viewing {startDate} - {endDate} | Last 30 days",
  },
});

export function Reporting() {
  const [activeOrganizationId] = useActiveOrganization();
  const { viewer: PortalViewer } = useViewer<BusinessViewer | TitleViewer>();
  const { user } = PortalViewer;

  const intl = useIntl();

  /* DATE FORMATTING */
  const todayStartedAt = startOfToday();
  const {
    thirtyDaysStartDate,
    today,
    priorPeriodStartDate,
    priorPeriodEndDate,
    sixMonthsStartDate,
  } = useMemo(() => {
    const todayISO = formatISO(todayStartedAt);
    const thirtyDaysStartDate = formatISO(subDays(todayStartedAt, 30));
    const priorPeriodStartDateISO = formatISO(subDays(todayStartedAt, 60));
    const priorPeriodEndDateISO = formatISO(subDays(todayStartedAt, 30));
    const sixMonthsStartDateISO = formatISO(subMonths(todayStartedAt, 6));
    return {
      thirtyDaysStartDate,
      today: todayISO,
      priorPeriodStartDate: priorPeriodStartDateISO,
      priorPeriodEndDate: priorPeriodEndDateISO,
      sixMonthsStartDate: sixMonthsStartDateISO,
    };
  }, [todayStartedAt]);
  const orgCreatedAt = useMemo(
    () => formatISO(startOfDay(new Date(user?.organization?.createdAt || sixMonthsStartDate))),
    [user?.organization, sixMonthsStartDate],
  );

  const viewingLast30Days = intl.formatMessage(MESSAGES.viewingLast30Days, {
    startDate: thirtyDaysStartDate
      ? new Date(thirtyDaysStartDate).toLocaleDateString("default", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "",
    endDate: today
      ? new Date(today).toLocaleDateString("default", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "",
  });

  // can also change interval to "daily" or "weekly" if needed
  // right now reports default to monthly intervals except for sentCompleted
  const queryVariables = useMemo(
    () => ({
      organizationId: activeOrganizationId!,
      sixMonthsStartDate,
      currentPeriodStartDate: thirtyDaysStartDate,
      today,
      priorPeriodStartDate,
      priorPeriodEndDate,
      allTimeStartDate: orgCreatedAt,
      sentCompletedInterval: ReportingIntervalEnum.MONTH,
      notarizationCountInterval: ReportingIntervalEnum.YEAR,
    }),
    [
      activeOrganizationId,
      thirtyDaysStartDate,
      today,
      priorPeriodStartDate,
      priorPeriodEndDate,
      sixMonthsStartDate,
      orgCreatedAt,
    ],
  );

  // @dev dependent query on organization createdAt date
  const { data, loading } = useQuery(ReportsDetails, {
    variables: queryVariables,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !orgCreatedAt || !activeOrganizationId,
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  if (data?.organization?.__typename !== "Organization") {
    throw new Error(`Expected Organization, got ${data?.organization?.__typename}`);
  }

  const { viewer, organization } = data;
  const {
    sentCompleted,
    completedDetails,
    priorPeriodCompleted,
    currentPeriodCompleted,
    notaryMeetingTime,
    notaryMeetingRating,
    notarizationCount,
  } = viewer;

  if (!isNotarizeSentCompletedScalarHashArray(sentCompleted)) {
    throw new Error("Expected sentCompleted to be an array of NotarizeSentCompletedScalarHash");
  }
  if (!isNotarizeCountScalarHashArray(completedDetails)) {
    throw new Error("Expected completedDetails to be an array of NotarizeCountScalarHash");
  }
  if (!isNotarizeCountScalarHashArray(priorPeriodCompleted)) {
    throw new Error("Expected priorPeriodCompleted to be an array of NotarizeCountScalarHash");
  }
  if (!isNotarizeCountScalarHashArray(currentPeriodCompleted)) {
    throw new Error("Expected currentPeriodCompleted to be an array of NotarizeCountScalarHash");
  }
  if (
    !isNotarizeSummaryScalarHashArray(notaryMeetingTime) ||
    !isNotarizeSummaryScalarHashArray(notaryMeetingRating)
  ) {
    throw new Error(
      "Expected notaryMeetingTime and notaryRating to be arrays of NotarizeSummaryScalarHash",
    );
  }
  if (!isNotarizeCountScalarHashArray(notarizationCount)) {
    throw new Error("Expected notarizationCount to be an array of NotarizeCountScalarHash");
  }

  return (
    <div className={Styles.page}>
      <div className={Styles.pageContent}>
        <Heading level="h1" textStyle="headingThree" className={Styles.title}>
          {intl.formatMessage(MESSAGES.title, {
            orgName: organization.name,
          })}
          <Badge kind="new" className={Styles.badge}>
            {intl.formatMessage(MESSAGES.titleTag)}
          </Badge>
        </Heading>
        <Paragraph textColor="subtle" className={Styles.description}>
          <span data-pendo-id="reporting-feedback-request">
            {intl.formatMessage(MESSAGES.subtitle)}
          </span>
        </Paragraph>
        <Hr />
        <div className={Styles.section}>
          <ValueSection notarizationCount={notarizationCount} />
          <SentCompletedSection
            chartData={sentCompleted}
            sixMonthsStartDate={sixMonthsStartDate}
            sixMonthsEndDate={today}
          />
          <CompletedDetailsSection
            completeDistributionReport={completedDetails}
            currentPeriodReport={currentPeriodCompleted}
            priorPeriodReport={priorPeriodCompleted}
            last30Days={viewingLast30Days}
            today={todayStartedAt}
          />
          <NotaryMeetingFeedbackSection
            notaryMeetingTime={notaryMeetingTime}
            notaryRating={notaryMeetingRating}
            last30Days={viewingLast30Days}
          />
        </div>
      </div>
    </div>
  );
}
